import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_BASE } from '../apiConfig';

const getStoredSession = () => localStorage.getItem('sessionId');

// Business session helpers
const getStoredBusinessSession = () => localStorage.getItem('businessSessionId');
const getStoredBusinessInfo = () => {
  try { return JSON.parse(localStorage.getItem('businessInfo') || 'null'); } catch { return null; }
};

const AuthContext = createContext({
  user: null,
  sessionId: null,
  login: () => {},
  logout: () => {},
  // Business context
  businessSessionId: null,
  businessInfo: null,
  isBusinessLoggedIn: false,
  businessLogin: () => {},
  businessLogout: () => {},
});

export const AuthProvider = ({ children }) => {
  // Never trust persisted profile data until the session is revalidated.
  const [user, setUser] = useState(null);
  const [sessionId, setSessionId] = useState(getStoredSession());

  // Business session state
  const [businessSessionId, setBusinessSessionId] = useState(getStoredBusinessSession());
  const [businessInfo, setBusinessInfo] = useState(getStoredBusinessInfo());

  useEffect(() => {
    if (user) localStorage.setItem('user', JSON.stringify(user));
    else localStorage.removeItem('user');
    if (sessionId) localStorage.setItem('sessionId', sessionId);
    else localStorage.removeItem('sessionId');
  }, [user, sessionId]);

  // Persist business session
  useEffect(() => {
    if (businessSessionId) localStorage.setItem('businessSessionId', businessSessionId);
    else localStorage.removeItem('businessSessionId');
    if (businessInfo) localStorage.setItem('businessInfo', JSON.stringify(businessInfo));
    else localStorage.removeItem('businessInfo');
  }, [businessSessionId, businessInfo]);

  // ── Session expiry detection ────────────────────────────────────────────
  const sessionExpiredRef = useRef(false);
  const sessionIdRef = useRef(sessionId);
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

  const handleSessionExpired = useCallback(() => {
    if (sessionExpiredRef.current || !sessionIdRef.current) return;
    sessionExpiredRef.current = true;
    localStorage.removeItem('sessionId');
    localStorage.removeItem('user');
    setUser(null);
    setSessionId(null);
    setTimeout(() => {
      alert('Your session has expired. Please sign in again.');
      window.location.href = '/login';
    }, 100);
  }, []);

  // Reset the expired flag when a new session is established
  useEffect(() => {
    if (sessionId) sessionExpiredRef.current = false;
  }, [sessionId]);

  // Validate personal session on mount
  useEffect(() => {
    if (!sessionId) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/profile`, {
          method: 'GET',
          headers: { 'x-session-id': sessionId },
        });
        if (!res.ok) {
          console.warn('[AuthContext] Session expired or invalid, logging out');
          localStorage.removeItem('sessionId');
          localStorage.removeItem('user');
          setUser(null);
          setSessionId(null);
        } else {
          const profile = await res.json();
          setUser(profile);
        }
      } catch {
        // Network error — keep existing state
      }
    })();
  }, []); // eslint-disable-line

  // Periodic session health check — detect expiry while the user is browsing
  useEffect(() => {
    if (!sessionId) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/profile`, {
          method: 'GET',
          headers: { 'x-session-id': sessionId },
        });
        if (!res.ok) {
          clearInterval(interval);
          handleSessionExpired();
        }
      } catch {
        // Network error — ignore, don't log out
      }
    }, 5 * 60 * 1000); // Check every 5 minutes
    return () => clearInterval(interval);
  }, [sessionId, handleSessionExpired]);

  // Validate business session on mount
  useEffect(() => {
    if (!businessSessionId) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/business-auth/session-info`, {
          method: 'GET',
          headers: { 'x-business-session': businessSessionId },
        });
        if (!res.ok) {
          console.warn('[AuthContext] Business session expired, clearing');
          setBusinessSessionId(null);
          setBusinessInfo(null);
          localStorage.removeItem('businessSessionId');
          localStorage.removeItem('businessInfo');
          localStorage.removeItem('businessEmployee');
          localStorage.removeItem('businessId');
        } else {
          const info = await res.json();
          setBusinessInfo(info);
        }
      } catch {
        // Network error — keep existing state
      }
    })();
  }, []);

  // Fetch user profile from backend
  const fetchProfile = async (session) => {
    try {
      const res = await fetch(`${API_BASE}/profile`, {
        method: 'GET',
        headers: { 'x-session-id': session },
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data;
    } catch {
      return null;
    }
  };

  // Call this after successful login/2FA/register
  const login = async (session) => {
    setSessionId(session);
    const profile = await fetchProfile(session);
    setUser(profile);
  };

  const logout = async () => {
    console.log('[AuthContext] logout called, sessionId:', sessionId);
    try {
      const res = await fetch(`${API_BASE}/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      });
      console.log('[AuthContext] /api/logout response:', res.status);
    } catch (err) {
      console.error('[AuthContext] /api/logout error:', err);
    }
    localStorage.removeItem('sessionId');
    localStorage.removeItem('user');
    setUser(null);
    setSessionId(null);
    console.log('[AuthContext] user and sessionId cleared');
  };

  // Business login — call after successful business auth
  const businessLogin = (bSessionId, info) => {
    setBusinessSessionId(bSessionId);
    setBusinessInfo(info);
  };

  // Business logout
  const businessLogout = async () => {
    try {
      await fetch(`${API_BASE}/business-auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-business-session': businessSessionId },
      });
    } catch { /* ignore */ }
    setBusinessSessionId(null);
    setBusinessInfo(null);
    localStorage.removeItem('businessSessionId');
    localStorage.removeItem('businessInfo');
    localStorage.removeItem('businessEmployee');
    localStorage.removeItem('businessId');
  };

  const isBusinessLoggedIn = Boolean(businessSessionId && businessInfo);

  // ── Global axios interceptor: detect expired sessions ───────────────────
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && sessionIdRef.current) {
          const url = error.config?.url || '';
          const isAuthEndpoint = /\/(login|register|forgot-password|reset-password|verify-2fa)/.test(url);
          if (!isAuthEndpoint) {
            handleSessionExpired();
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, [handleSessionExpired]);

  return (
    <AuthContext.Provider value={{
      user, sessionId, login, logout,
      businessSessionId, businessInfo, isBusinessLoggedIn, businessLogin, businessLogout,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
